import { useState } from "react";
import { patchApp } from "../lib/api-admin";

/**
 * App name with a subtle pencil: click to rename inline. Saves `display_name` through the same admin PATCH as the
 * Settings "Weergavenaam" card; an empty value clears the override (back to the App Store Connect name).
 * Renders phrasing content only so it can live inside an <h1>.
 */
export function EditableAppName({ app, onSaved }: { app: { id: string; name: string; display_name: string | null }; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const shown = app.display_name ?? app.name;

  function start() {
    setDraft(app.display_name ?? "");
    setErr(null);
    setEditing(true);
  }
  async function save() {
    const next = draft.trim();
    if (next === (app.display_name ?? "")) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await patchApp(app.id, { display_name: next === "" ? null : next });
      setEditing(false);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Opslaan mislukt");
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <span className="name-edit">
        <span className="name-edit-text">{shown}</span>
        <button type="button" className="name-edit-btn" onClick={start} aria-label="Naam wijzigen" title="Naam wijzigen">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
          </svg>
        </button>
      </span>
    );
  }
  return (
    <span className="name-edit name-edit-open">
      <input
        className="search name-edit-input"
        type="text"
        maxLength={200}
        autoFocus
        value={draft}
        placeholder={app.name}
        aria-label="Weergavenaam"
        disabled={busy}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void save();
          if (e.key === "Escape") setEditing(false);
        }}
      />
      <button type="button" className="pill pill-primary" disabled={busy} onClick={() => void save()}>
        Opslaan
      </button>
      <button type="button" className="pill" disabled={busy} onClick={() => setEditing(false)}>
        Annuleer
      </button>
      {err && <span className="name-edit-err" role="alert">{err}</span>}
    </span>
  );
}
